import { faFacebookSquare } from '@fortawesome/free-brands-svg-icons/faFacebookSquare';
import { faInstagram } from '@fortawesome/free-brands-svg-icons/faInstagram';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons/faLinkedin';
import { faSquareXTwitter } from '@fortawesome/free-brands-svg-icons/faSquareXTwitter';
import { faTiktok } from '@fortawesome/free-brands-svg-icons/faTiktok';
import { faYoutubeSquare } from '@fortawesome/free-brands-svg-icons/faYoutubeSquare';
import { faClock } from '@fortawesome/pro-light-svg-icons/faClock';
import { faLaptop } from '@fortawesome/pro-light-svg-icons/faLaptop';
import { faMapMarkerAlt } from '@fortawesome/pro-light-svg-icons/faMapMarkerAlt';
import { BLACK, FB_BLUE, LINKED_IN_BLUE, YOUTUBE_RED } from '@propertypal/shared/src/styles/theme';
import { Agent } from '@propertypal/shared/src/types/agents';
import getOpeningTimes from '@propertypal/shared/src/utils/agent/getOpeningTimes';
import React, { FunctionComponent } from 'react';
import { useTheme } from 'styled-components';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { LinkText, Text } from '../typography';
import { Container, Icon, Row, Title, SocialIcons, Content, Column } from './BranchOverview.style';
import ContactAgent from './ContactAgent';

interface Props {
  agent: Agent;
  compact?: boolean;
}

const hasSocialMedia = (agent: Agent) => {
  if (!agent.profile) {
    return false;
  }

  const { facebookUrl, twitterUrl, linkedInUrl, instagramUrl, pinterestUrl, youtubeUrl } = agent.profile;

  return facebookUrl || twitterUrl || linkedInUrl || instagramUrl || pinterestUrl || youtubeUrl;
};

const BranchOverview: FunctionComponent<Props> = (props) => {
  const { profile, showAddress } = props.agent;
  const theme = useTheme();
  const timeLabels = getOpeningTimes(props.agent.openingTimes);

  return (
    <Container compact={props.compact}>
      <Content compact={props.compact}>
        <Column compact={props.compact}>
          <Title>{props.compact ? props.agent.organisationBranch : 'Contact details'}</Title>

          {showAddress && (
            <Row data-testid="agent-address">
              <Icon>
                <FontAwesomeIcon icon={faMapMarkerAlt} color={theme.textDark} style={{ fontSize: 28 }} />
              </Icon>

              <Text>
                {props.agent.addressLine1}, {props.agent.addressLine2} <br />
                {props.agent.town}, {props.agent.postcode}
              </Text>
            </Row>
          )}

          {props.agent.websiteUrl && (
            <Row data-testid="agent-website">
              <Icon>
                <FontAwesomeIcon icon={faLaptop} color={theme.textDark} style={{ fontSize: 22 }} />
              </Icon>

              <LinkText
                href={props.agent.websiteUrl}
                rel="noreferrer"
                target="_blank"
                style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {props.agent.websiteUrl.replace('https://', '').replace('http://', '').replace('www.', '')}
              </LinkText>
            </Row>
          )}

          {props.agent.officePhone && props.agent.officePhone.displayNumber && (
            <ContactAgent
              containerStyle={{ margin: '30px 0 15px', maxWidth: 260 }}
              agent={props.agent}
              page="Account Landing Page"
              location="Main"
            />
          )}
        </Column>

        {timeLabels.length > 0 && (
          <Column compact={props.compact}>
            {!props.compact && <Title>Opening Times</Title>}

            <Row>
              <Icon>
                <FontAwesomeIcon icon={faClock} color={theme.textDark} size={22} />
              </Icon>

              <div>
                {timeLabels.map((timeLabel) => (
                  <div key={timeLabel.firstDay}>
                    <Text fontWeight="600">
                      {timeLabel.firstDay}
                      {timeLabel.lastDay && ` to ${timeLabel.lastDay}`}:
                    </Text>
                    <Text color={theme.textLight} mb={10}>
                      {timeLabel.openingTime} {timeLabel.closingTime && `- ${timeLabel.closingTime}`}
                    </Text>
                  </div>
                ))}
              </div>
            </Row>
          </Column>
        )}

        {profile && hasSocialMedia(props.agent) && (
          <Column compact={props.compact}>
            {!props.compact && <Title>Social Media</Title>}

            <SocialIcons compact={props.compact}>
              {profile.facebookUrl && (
                <a href={profile.facebookUrl} target="_blank" rel="noreferrer">
                  <FontAwesomeIcon icon={faFacebookSquare} color={FB_BLUE} style={{ fontSize: 32 }} />
                </a>
              )}

              {profile.twitterUrl && (
                <a href={profile.twitterUrl} target="_blank" rel="noreferrer">
                  <FontAwesomeIcon icon={faSquareXTwitter} color={theme.black} style={{ fontSize: 32 }} />
                </a>
              )}

              {profile.instagramUrl && (
                <a href={profile.instagramUrl} target="_blank" rel="noreferrer">
                  <FontAwesomeIcon icon={faInstagram} style={{ fontSize: 32 }} />
                </a>
              )}

              {profile.youtubeUrl && (
                <a href={profile.youtubeUrl} target="_blank" rel="noreferrer">
                  <FontAwesomeIcon icon={faYoutubeSquare} color={YOUTUBE_RED} style={{ fontSize: 32 }} />
                </a>
              )}

              {profile.linkedInUrl && (
                <a href={profile.linkedInUrl} target="_blank" rel="noreferrer">
                  <FontAwesomeIcon icon={faLinkedin} color={LINKED_IN_BLUE} style={{ fontSize: 32 }} />
                </a>
              )}

              {profile.tiktokUrl && (
                <a href={profile.tiktokUrl} target="_blank" rel="noreferrer">
                  <FontAwesomeIcon icon={faTiktok} color={BLACK} style={{ fontSize: 28 }} />
                </a>
              )}
            </SocialIcons>
          </Column>
        )}
      </Content>
    </Container>
  );
};

export default BranchOverview;
